import {
    Backdrop,
    Box,
    CircularProgress,
    Stack,
    useTheme,
} from "@mui/material";
import mapboxgl from "mapbox-gl";
import { useContext, useEffect, useRef, useState } from "react";
import { AdDetailDto } from "../../../api/psc";
import { CardInformation } from "../../../components/exports";
import { MapCenter, MapDefaultZoom } from "../../../constants/MapConstants";
import {
    DefaultDarkMapStyles,
    DefaultMapStyles,
} from "../../../constants/ProjectConstants";
import { DialogContext } from "../../../contexts/DialogueContext";
import AdProjectDetail from "../dialogs/AdProjectDetail";

export default function AdDetailMap(props: {
    ad: AdDetailDto;
    region: number;
}) {
    const map = useRef(null);
    const mapContainer = useRef(null);

    const [isMapReady, setIsMapReady] = useState(false);
    const [markers, setMarkers] = useState([]);

    const theme = useTheme();

    const { openDialog } = useContext(DialogContext);

    useEffect(() => {
        initializeMap();
    }, []);

    useEffect(() => {
        setupMap();
    }, [props.region, isMapReady]);

    const initializeMap = () => {
        const style =
            theme.palette.mode === "light"
                ? DefaultMapStyles
                : DefaultDarkMapStyles;

        try {
            mapboxgl.setRTLTextPlugin(
                process.env.REACT_APP_MAP_STYLE_URL,
                null
            );
        } catch {}

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: style,
            center: MapCenter,
            zoom: MapDefaultZoom,
        });

        if (map.current.loaded()) {
            setupMap();
        } else {
            map.current.on("load", () => {
                setIsMapReady(true);
            });
        }
    };

    const setupMap = () => {
        if (!isMapReady) {
            return;
        }

        const points = props.ad.regions
            .filter((i) => props.region === 0 || i.region.id === props.region)
            .map((r) => r.projectPhotos)
            .reduce((i, j) => i.concat(j), []);

        for (const marker of markers) {
            marker.remove();
        }

        const addedMarkers = [];

        for (const point of points) {
            const marker = new mapboxgl.Marker().setLngLat([
                point.longitude,
                point.latitude,
            ]);
            addedMarkers.push(marker);
            marker.addTo(map.current);

            marker.getElement().addEventListener("click", () => {
                openDialog(<AdProjectDetail item={point} />);
            });
        }

        setMarkers(addedMarkers);
    };

    return (
        <Box
            sx={{
                position: "fixed",
                zIndex: -1,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            }}
            ref={mapContainer}
        >
            <Backdrop open={props.ad === null}>
                <CircularProgress sx={{ color: "white" }} />
            </Backdrop>

            <Box
                sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    bgcolor: "background.paper",
                    zIndex: 1,
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
            >
                <Stack spacing={1} sx={{ p: 2 }}>
                    <CardInformation
                        title="تعداد کل درخواست شده"
                        value={props.ad.ad.requestedCount}
                    />
                    <CardInformation
                        title="تعداد کل نصب شده"
                        value={props.ad.ad.completedCount}
                    />
                    {props.ad.regions.map((r) => {
                        return (
                            <CardInformation
                                key={r.region.id}
                                title={`تعداد نصب شده در ${r.region.title}`}
                                value={r.completedCount}
                            />
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
}
